import React from 'react';
import { DollarSign, Zap, Compass, Star } from 'lucide-react';

const SpecialLagnasDisplay = ({ specialLagnasData }) => {
  if (!specialLagnasData) return null;

  const lagnas = specialLagnasData["7.1_special_lagnas"] || {};
  const glHlWealth = lagnas.GL_HL_wealth_power;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 mb-2">
          Special Lagnas & Points
        </h2>
        <p className="text-white/70 text-sm">
          Parāśara + Jaimini Systems for Dynamic Activity, Wealth, Power & Spiritual Timing
        </p>
      </div>

      {/* GL+HL Wealth & Power Metric */}
      {glHlWealth && (
        <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 rounded-2xl p-6 border-2 border-green-500/50 shadow-lg">
          <h3 className="text-green-400 text-2xl font-bold mb-6 flex items-center gap-2">
            <DollarSign className="w-7 h-7" /> GL+HL Wealth & Power Indicator
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white/5 rounded-xl p-5 border border-green-500/30">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                <p className="text-white/60 text-sm">Ghaṭika Lagna (Power)</p>
              </div>
              <p className="text-yellow-400 font-bold text-2xl">{glHlWealth.ghati_lagna_sign}</p>
            </div>
            
            <div className="bg-white/5 rounded-xl p-5 border border-green-500/30">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-green-400" />
                <p className="text-white/60 text-sm">Hora Lagna (Wealth)</p>
              </div>
              <p className="text-green-400 font-bold text-2xl">{glHlWealth.hora_lagna_sign}</p>
            </div>
            
            <div className="bg-white/5 rounded-xl p-5 border border-green-500/30">
              <div className="flex items-center gap-2 mb-2">
                <Compass className="w-5 h-5 text-cyan-400" />
                <p className="text-white/60 text-sm">House Distance</p>
              </div>
              <p className="text-cyan-400 font-bold text-2xl">{glHlWealth.house_distance}</p>
            </div>
          </div>

          <div className={`rounded-xl p-6 border-2 ${
            glHlWealth.strength === 'Strong' ? 'bg-green-500/20 border-green-500/50' :
            glHlWealth.strength === 'Moderate' ? 'bg-yellow-500/20 border-yellow-500/50' :
            'bg-red-500/20 border-red-500/50'
          }`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white text-lg font-bold">Overall Strength:</h4>
              <div className={`px-4 py-2 rounded-lg font-bold text-xl ${
                glHlWealth.strength === 'Strong' ? 'bg-green-500/30 text-green-300' :
                glHlWealth.strength === 'Moderate' ? 'bg-yellow-500/30 text-yellow-300' :
                'bg-red-500/30 text-red-300'
              }`}>
                {glHlWealth.strength}
              </div>
            </div>
            <p className="text-white/80 mb-2">{glHlWealth.interpretation}</p>
            <div className="bg-white/10 rounded-lg p-3 mt-3">
              <p className="text-white/70 text-sm"><span className="font-bold">Usage:</span> {glHlWealth.usage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Special Lagnas Grid */}
      <div>
        <h3 className="text-cyan-400 text-2xl font-bold mb-4">All Special Lagnas</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(lagnas).map(([key, lagnaData]) => {
            // Skip the GL_HL metric as it's displayed separately
            if (key === 'GL_HL_wealth_power' || !lagnaData || typeof lagnaData !== 'object') return null;

            return (
              <div key={key} className="bg-gradient-to-br from-slate-900/80 to-slate-800/50 rounded-xl p-4 border border-cyan-500/30 hover:scale-105 transition-transform">
                <div className="flex items-center gap-2 mb-3">
                  <Star className="w-4 h-4 text-cyan-400" />
                  <h4 className="text-cyan-400 font-bold">{key.replace(/([A-Z])/g, ' $1').trim()}</h4>
                </div>
                
                <div className="space-y-2">
                  {lagnaData.sign && (
                    <div className="bg-white/5 rounded-lg p-2">
                      <p className="text-white/60 text-xs">Sign</p>
                      <p className="text-yellow-400 font-bold">{lagnaData.sign}</p>
                    </div>
                  )}
                  
                  {lagnaData.description && (
                    <p className="text-white/70 text-xs italic">{lagnaData.description}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SpecialLagnasDisplay;
